import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import * as LmsActions from './lms-activities.actions';
import { switchMap, catchError, map } from 'rxjs/operators';
import { of, forkJoin, Observable, concat } from 'rxjs';
import { LmsActivitiesProcessFlowService } from '../../../services/lms-activities-process-flow.service';
import _ from 'lodash';
import * as AuthActions from '../../../auth/store/actions/auth.actions';

@Injectable()
export class LmsEffects {
  @Effect()
  getLabInfo = this.actions.pipe(
    ofType(LmsActions.GET_LAB_INFO),
    switchMap((payload: any) =>
      concat(
        of(new AuthActions.ShowLoading()),
        this.lmsService.getLabInfo(payload.labCapNumber)
      ).pipe(
        switchMap((action): Observable<any> => {
          console.log(action);
          if (!action.type) {
            return concat(
              of(new LmsActions.GetLabInfoSuccess(action)),
              of(new AuthActions.HideLoading())
            );
          } else if (action.type === AuthActions.AuthActionTypes.SHOW_LOADING) {
            return of(new AuthActions.ShowLoading());
          }
        }),
        catchError((error) =>
          concat(
            of(new LmsActions.GetLabInfoFailure(error)),
            of(new AuthActions.HideLoading())
          )
        )
      )
    )
  );

  @Effect()
  affiliateUserToLab = this.actions.pipe(ofType(LmsActions.AFFILIATE_USER_TO_LAB),
    switchMap((payload: any) =>
      forkJoin([of(payload),
        this.lmsService.getKitsForLab(payload.activity.lmsActivityId, payload.labCapNumber),
        this.lmsService.getAssignedKits(payload.labCapNumber, payload.mailingCode)]
        ).switchMap((data: any) => {
          const [obj, kits, numKitsAssigned] = data;
          if (kits.length === 0 && obj.activity.processFlowType !== '1:M/PURCHASE') {
            return of(new LmsActions.KitsNotPurchased());
          } else if (!_.isEmpty(kits) && !kits[0].fulfilled) {
            return of(new LmsActions.KitsUnfulfilled());
          } else if (numKitsAssigned < kits.length || obj.activity.processFlowType === '1:M' || obj.activity.processFlowType === '1:M/PURCHASE') {
            return this.lmsService.affiliateUserToLab(obj.labCapNumber);
          } else if (numKitsAssigned === kits.length) {
            return of(new LmsActions.AllKitsUsed());
          }
        }
        ).pipe(
          switchMap((action: any): Observable<any> => {
            if (_.isNil(action)) {
              return concat(of(new AuthActions.HideLoading()), of(new LmsActions.AffiliateUserToLabSuccess()));
            } else {
              switch (action.type) {
                case LmsActions.AFFILIATE_USER_TO_LAB_SUCCESS:
                  return concat(of(new AuthActions.HideLoading()), of(new LmsActions.AffiliateUserToLabSuccess()));
                case LmsActions.KITS_NOT_PURCHASED:
                  return concat(of(new AuthActions.HideLoading()), of(new LmsActions.KitsNotPurchased()));
                case LmsActions.ALL_KITS_USED:
                  return concat(of(new AuthActions.HideLoading()), of(new LmsActions.AllKitsUsed()));
                case LmsActions.KITS_UNFULFILLED:
                  return concat(of(new AuthActions.HideLoading()), of(new LmsActions.KitsUnfulfilled()));
              }
            }
          })
        )
    )
  );

  @Effect()
  affiliateUserToLabQmed = this.actions
    .pipe(
      ofType(LmsActions.AFFILIATE_USER_TO_LAB_QMED),
      switchMap((payload: any) =>
        this.lmsService
          .getLabsHavingPurchasedQmed(
            payload.activity.activityCode,
            payload.labCapNumber
          )
          .pipe(
            switchMap((labs: any) => {
              if (_.isEmpty(labs)) {
                return of(new LmsActions.KitsNotPurchased());
              }
              return this.lmsService.affiliateUserToLab(payload.labCapNumber);
            })
          )
      )
    )
    .pipe(
      switchMap((action: any): Observable<any> => {
        if (_.isNil(action)) {
          return concat(
            of(new AuthActions.HideLoading()),
            of(new LmsActions.AffiliateUserToLabSuccess())
          );
        } else if (action.type === LmsActions.KITS_NOT_PURCHASED) {
          return of(new LmsActions.KitsNotPurchased());
        }
      })
    );

  @Effect()
  loadOnAffiliate = this.actions.pipe(
    ofType(LmsActions.AFFILIATE_USER_TO_LAB),
    switchMap((action: any) => of(new AuthActions.ShowLoading()))
  );

  @Effect()
  getKitsForLab = this.actions.pipe(
    ofType(LmsActions.GET_KITS_FOR_LAB),
    switchMap((payload: any) =>
      this.lmsService
        .getKitsForLab(payload.activityId, payload.labCapNumber)
        .pipe(
          map((kits) => {
            return new LmsActions.GetKitsForLabSuccess(kits);
          })
        )
    )
  );

  @Effect()
  assignKitToUser = this.actions.pipe(
    ofType(LmsActions.ASSIGN_KIT_TO_USER),
    switchMap((payload: any) =>
      concat(
        of(new AuthActions.ShowLoading()),
        this.lmsService
          .assignKitToUser(payload.activityId, payload.labCapNumber)
          .pipe(
            map(
              (assignmentResponse) =>
                new LmsActions.AssignKitToUserSuccess(assignmentResponse)
            )
          )
      )
    ),
    catchError((error) =>
      concat(
        of(new AuthActions.HideLoading()),
        of(new LmsActions.AssignKitToUserFailure(error))
      )
    )
  );

  @Effect()
  getLabSelectData = this.actions.pipe(
    ofType(LmsActions.GET_LAB_SELECT_DATA),
    switchMap((payload: any) => {
      return concat(
        of(new AuthActions.ShowLoading()),
        this.lmsService
          .getLabSelectData(payload.activityCode)
          .pipe(
            switchMap((labSelectData) =>
              concat(
                of(new LmsActions.GetLabSelectDataSuccess(labSelectData)),
                of(new AuthActions.HideLoading())
              )
            )
          )
      );
    })
  );

  constructor(
    private actions: Actions,
    private lmsService: LmsActivitiesProcessFlowService
  ) { }
}
